import React, { useRef, useState, useMemo } from "react";
import { Popover, Button, Divider, Tag } from "@livingdesign/react";
import { ChevronDown, Gear, Search, Plus } from "@livingdesign/icons";
import { TEAM } from "../../../consts/DBFields";
import { LOCAL_STORAGE } from "../../../consts/Common";
import { useAppContext } from "../../../contexts/Core";
import Configs from "../../../Configurations";
import "./TeamSelector.scss";

const SPARK_TEAM_KEY = "spark_selected_team_id";

const AVATAR_COLORS = [
  "#0053E2",
  "#6B2FBB",
  "#C4281A",
  "#0D6B3D",
  "#9E5A00",
  "#005F73",
];

function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function navigateToSparkComms(path) {
  const baseUrl = Configs.REACT_APP_SPARK_COMMS_URL;
  if (!baseUrl) {
    console.error(
      "[TeamSelector] REACT_APP_SPARK_COMMS_URL is not configured; cannot navigate to:",
      path
    );
    return;
  }
  window.location.href = `${baseUrl}/${path}`;
}

const QA_ID_PREFIX = "TeamSelector";

function TeamSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const triggerRef = useRef(null);

  const { selectedTeam, userTeams, setSelectedTeam } = useAppContext();

  const activeTeams = userTeams || [];

  const filteredTeams = useMemo(() => {
    const sorted = [...activeTeams].sort((a, b) => {
      if (a[TEAM.ID] === selectedTeam?.[TEAM.ID]) return -1;
      if (b[TEAM.ID] === selectedTeam?.[TEAM.ID]) return 1;
      return a[TEAM.NAME].localeCompare(b[TEAM.NAME]);
    });
    if (!searchTerm.trim()) return sorted;
    const lower = searchTerm.toLowerCase();
    return sorted.filter((t) =>
      t[TEAM.NAME].toLowerCase().includes(lower)
    );
  }, [activeTeams, searchTerm, selectedTeam]);

  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    localStorage.setItem(LOCAL_STORAGE.RECENT_TEAM_ID, team[TEAM.ID]);
    localStorage.setItem(SPARK_TEAM_KEY, team[TEAM.ID]);
    setIsOpen(false);
  };

  const handleTeamSettings = (team, e) => {
    e.stopPropagation();
    if (team[TEAM.ID] !== selectedTeam?.[TEAM.ID]) {
      setSelectedTeam(team);
      localStorage.setItem(LOCAL_STORAGE.RECENT_TEAM_ID, team[TEAM.ID]);
      localStorage.setItem(SPARK_TEAM_KEY, team[TEAM.ID]);
    }
    setIsOpen(false);
    navigateToSparkComms(`teams/${team[TEAM.ID]}/settings`);
  };

  const handleCreateTeam = () => {
    setIsOpen(false);
    navigateToSparkComms("teams/create");
  };

  const handleFindTeam = () => {
    setIsOpen(false);
    navigateToSparkComms("teams?tab=all");
  };

  // Hidden when user has no active teams
  if (activeTeams.length === 0) return null;

  return (
    <Popover
      position="bottomCenter"
      hasNubbin={false}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      UNSAFE_className="team-selector-popover-align"
      triggerRef={triggerRef}
      data-testid={`${QA_ID_PREFIX}-Popover`}
      content={
        <div
          className="team-selector-container"
          data-testid={`${QA_ID_PREFIX}-Container`}
        >
          <div className="team-selector-title">My Teams</div>

          <div className="team-selector-search-wrapper">
            <span className="team-selector-search-icon" aria-hidden="true">
              <Search size="small" />
            </span>
            <input
              className="team-selector-search-input"
              type="text"
              placeholder="Search my teams"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search my teams"
              data-testid={`${QA_ID_PREFIX}-SearchInput`}
            />
          </div>

          <ul
            className="team-selector-list"
            role="listbox"
            aria-label="My teams"
          >
            {filteredTeams.length === 0 ? (
              <li className="team-selector-no-results">No teams found</li>
            ) : (
              filteredTeams.map((team) => (
                <li
                  key={team[TEAM.ID]}
                  className={`team-selector-row${
                    team[TEAM.ID] === selectedTeam?.[TEAM.ID]
                      ? " team-selector-row-selected"
                      : ""
                  }`}
                  role="option"
                  aria-selected={team[TEAM.ID] === selectedTeam?.[TEAM.ID]}
                  onClick={() => handleSelectTeam(team)}
                  data-testid={`${QA_ID_PREFIX}-Row-${team[TEAM.ID]}`}
                >
                  <div
                    className="team-selector-avatar"
                    style={{ backgroundColor: getAvatarColor(team[TEAM.NAME]) }}
                    aria-hidden="true"
                  >
                    {team[TEAM.NAME].charAt(0).toUpperCase()}
                  </div>
                  <span className="team-selector-name">{team[TEAM.NAME]}</span>
                  {team[TEAM.ID] === selectedTeam?.[TEAM.ID] && (
                    <Tag color="info" variant="secondary">Current</Tag>
                  )}
                  <button
                    className="team-selector-settings-btn"
                    onClick={(e) => handleTeamSettings(team, e)}
                    aria-label={`Settings for ${team[TEAM.NAME]}`}
                    data-testid={`${QA_ID_PREFIX}-Settings-${team[TEAM.ID]}`}
                  >
                    <Gear size="small" aria-hidden="true" />
                  </button>
                </li>
              ))
            )}
          </ul>

          <div className="team-selector-divider"><Divider /></div>

          <div className="team-selector-actions">
            <Button
              variant="secondary"
              size="medium"
              isFullWidth
              onClick={handleCreateTeam}
              UNSAFE_className="team-selector-action-btn"
              UNSAFE_style={{ fontSize: "14px", lineHeight: "20px" }}
              data-testid={`${QA_ID_PREFIX}-CreateTeamBtn`}
            >
              <Plus size="small" aria-hidden="true" /> Create team
            </Button>
            <Button
              variant="primary"
              size="medium"
              isFullWidth
              onClick={handleFindTeam}
              UNSAFE_className="team-selector-action-btn"
              UNSAFE_style={{ fontSize: "14px", lineHeight: "20px" }}
              data-testid={`${QA_ID_PREFIX}-FindTeamBtn`}
            >
              <Search size="small" aria-hidden="true" /> Find a team
            </Button>
          </div>
        </div>
      }
    >
      <div
        ref={triggerRef}
        className={`team-selector-trigger${isOpen ? " team-selector-trigger-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Selected team: ${selectedTeam?.[TEAM.NAME] ?? ""}`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
        data-testid={`${QA_ID_PREFIX}-Trigger`}
      >
        {selectedTeam?.[TEAM.NAME] && (
          <div
            className="team-selector-trigger-avatar"
            style={{ backgroundColor: getAvatarColor(selectedTeam[TEAM.NAME]) }}
            aria-hidden="true"
          >
            {selectedTeam[TEAM.NAME].charAt(0).toUpperCase()}
          </div>
        )}
        <span className="team-selector-trigger-name">
          {selectedTeam?.[TEAM.NAME]}
        </span>
        <ChevronDown
          size="small"
          aria-hidden="true"
          className={`team-selector-trigger-chevron${
            isOpen ? " team-selector-trigger-chevron-open" : ""
          }`}
        />
      </div>
    </Popover>
  );
}

export default TeamSelector;
